/**
 * One objective: the curriculum as stored, the research it was built from in full,
 * the manifests that pinned each pass, and the three re-runs. F-82, F-83.
 *
 * English-only, no `t()`. See the note at the top of `Console.tsx`.
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  PASS_COSTS,
  getObjective,
  isError,
  rerun,
  type AdminNode,
  type AdminObjectiveDetail,
  type ApiError,
  type ResearchDocument,
} from './api';
import { ConfirmedAction, Failed, Id, Waiting, ago, fixed, localTitle, stamp } from './parts';
import { LOG_GROUPS, logsRecent } from './logs';

/**
 * Where the depth walk stops, whatever `parentId` claims.
 *
 * The same ceiling the learner's `Curriculum` keeps, for the same reason: a
 * `parentId` chain that loops would climb for ever and take the page with it. Here it
 * only costs an indentation level, because the console renders every node as its own
 * row regardless of where the walk thinks it belongs.
 */
const MAX_DEPTH = 12;

/**
 * How deep a node sits, from its parent chain.
 *
 * Nodes arrive in the API's order — `gsi3` carries the order zero-padded so a
 * lexicographic sort matches the learner's chosen order — so this screen never sorts
 * them, it only works out how far to indent each one. Re-sorting here would be a
 * second opinion about ordering that could quietly disagree with the pipeline's.
 */
function depthOf(node: AdminNode, byId: Map<string, AdminNode>): number {
  let depth = 0;
  let parentId = node.parentId;
  const seen = new Set<string>([node.nodeId]);
  while (parentId && depth < MAX_DEPTH) {
    if (seen.has(parentId)) return depth;
    seen.add(parentId);
    const parent = byId.get(parentId);
    if (!parent) return depth;
    depth += 1;
    parentId = parent.parentId;
  }
  return depth;
}

/** The stored title, in whichever language exists. The console is not translating anything. */
const titleOf = (node: AdminNode): string => localTitle(node.title);

const summaryOf = (node: AdminNode): string => localTitle(node.summary, '');

/**
 * The curriculum, read-only, with the identifiers showing.
 *
 * **Not the learner's `Curriculum` component, and the reasons are structural rather
 * than cosmetic.** It writes — its checkboxes POST `/api/curriculum/active` as the
 * signed-in learner, which from the console would mean the operator silently editing
 * somebody else's plan, against D-19. It renders every string through `t()`, which
 * this directory deliberately does not use. It shows weight as a band and a bar
 * because a learner shown `0.72` next to `0.68` reads a precision the model cannot
 * support — but that precision is exactly what an operator is judging synthesis by,
 * so here the number is the point. And it hides `nodeId`, which is the value needed
 * to filter the item list and to query DynamoDB by hand.
 *
 * Two components rather than a `readOnly` prop on one: the learner's version would
 * grow a branch through every one of those four decisions, and a shared component
 * whose every line is conditional is two components in a trench coat.
 */
function Tree({ nodes, objectiveId }: { nodes: AdminNode[]; objectiveId: string }) {
  const byId = new Map(nodes.map((node) => [node.nodeId, node]));
  const off = nodes.filter((node) => node.active === false).length;

  return (
    <section className="admin-panel">
      <header className="admin-head">
        <h2>Curriculum</h2>
        <p className="sub">
          {nodes.length} nodes{off > 0 ? ` · ${off} switched off` : ''}
        </p>
      </header>

      {nodes.length === 0 ? (
        <p className="admin-empty">No curriculum has been built for this objective.</p>
      ) : (
        <div className="admin-scroll">
          <table className="admin-table tree">
            <thead>
              <tr>
                <th>Node</th>
                <th>Weight</th>
                <th>Active</th>
                <th>Order</th>
                <th>nodeId</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {nodes.map((node) => {
                const depth = depthOf(node, byId);
                return (
                  <tr key={node.nodeId} className={node.active === false ? 'is-off' : undefined}>
                    <td>
                      {/* Indented by padding rather than by nested tables, so every
                          node stays one row that a table can align — the operator is
                          comparing weights down a column, not admiring the tree. */}
                      <span
                        className="stack"
                        style={{ paddingLeft: `${Math.min(depth, MAX_DEPTH) * 1.1}rem` }}
                      >
                        <span className={depth === 0 ? 'strong' : undefined}>{titleOf(node)}</span>
                        {summaryOf(node) && <span className="sub">{summaryOf(node)}</span>}
                      </span>
                    </td>
                    <td className="num">{fixed(node.weight)}</td>
                    <td>
                      {node.active === false ? (
                        <span className="tag warn">off</span>
                      ) : (
                        <span className="sub">on</span>
                      )}
                    </td>
                    <td className="num">{node.order}</td>
                    <td>
                      <Id value={node.nodeId} />
                    </td>
                    <td>
                      <Link
                        className="admin-link"
                        to={`/admin/objective/${objectiveId}/items?node=${encodeURIComponent(node.nodeId)}`}
                      >
                        items →
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/**
 * The research documents, whole.
 *
 * Raw text in a scrolling block rather than rendered Markdown, for the reason
 * `Research.tsx` gives: the value of the document here is that it is the exact text
 * the curriculum was built from, tables and inline URLs included, and a renderer that
 * swallowed a malformed table would hide the thing worth checking.
 *
 * Collapsed per document, and the character count is on the summary. One pass is
 * ~48,000 characters; expanded by default it would push the curriculum, the manifests
 * and every re-run button off the bottom of the screen.
 */
function ResearchDocs({ research }: { research: ResearchDocument[] }) {
  const total = research.reduce((sum, doc) => sum + (doc.characters ?? 0), 0);
  return (
    <section className="admin-panel">
      <header className="admin-head">
        <h2>Research</h2>
        <p className="sub">
          {research.length} document{research.length === 1 ? '' : 's'} ·{' '}
          {total.toLocaleString('en')} characters
        </p>
      </header>

      {research.length === 0 ? (
        <p className="admin-empty">Nothing in the content store for this objective.</p>
      ) : (
        research.map((doc) => (
          <details key={doc.key} className="doc">
            <summary>
              <span className="strong">{doc.title || doc.key}</span>
              <span className="sub">
                {doc.origin} · {doc.language} · {(doc.characters ?? 0).toLocaleString('en')} chars ·{' '}
                {stamp(doc.producedAt)}
                {doc.truncated ? ' · TRUNCATED' : ''}
              </span>
            </summary>
            <p className="sub">
              <Id value={doc.key} />
            </p>
            <pre className="doc-body">
              {doc.body}
              {doc.truncated ? '\n\n… truncated by the API' : ''}
            </pre>
          </details>
        ))
      )}
    </section>
  );
}

/**
 * The manifests, as JSON.
 *
 * Not parsed into fields, deliberately: a manifest's job is to pin exactly what a
 * pass used, its shape is not fixed by the admin contract, and a reader that picked
 * out the four keys it knew about would silently drop whatever was added last week —
 * which is the field most likely to be the one being investigated.
 */
function Manifests({ manifests }: { manifests: Record<string, unknown>[] }) {
  return (
    <section className="admin-panel">
      <header className="admin-head">
        <h2>Manifests</h2>
        <p className="sub">{manifests.length}</p>
      </header>
      {manifests.length === 0 ? (
        <p className="admin-empty">No manifests recorded.</p>
      ) : (
        manifests.map((manifest, index) => (
          <details key={index} className="doc">
            <summary>
              <span className="strong">
                {String(manifest.pass ?? manifest.kind ?? `manifest ${index + 1}`)}
              </span>
              <span className="sub">{String(manifest.producedAt ?? manifest.at ?? '')}</span>
            </summary>
            <pre className="doc-body">{JSON.stringify(manifest, null, 2)}</pre>
          </details>
        ))
      )}
    </section>
  );
}

/**
 * What each pass last recorded — the success line, or the failure — and its log.
 *
 * The log links carry a 24-hour window rather than opening the group, because
 * nothing on the objective records *when* a pass ran: only what it produced. A day is
 * the honest window for "the failure I am reading about happened recently", and the
 * label says so rather than implying precision it has not got. A send, which does know
 * its own minute, gets a ten-minute window instead — see the timeline.
 *
 * `items` is three log groups because generation is three functions: submit the batch,
 * poll it, retrieve it. A single link would be the wrong one two times out of three.
 */
function PassState({ detail }: { detail: AdminObjectiveDetail }) {
  const objective = detail.objective;
  const passes: {
    name: string;
    detail?: string;
    error?: string;
    logs: { label: string; group: string }[];
  }[] = [
    {
      name: 'research',
      detail: objective.researchDetail,
      error: objective.researchError,
      logs: [{ label: 'research', group: LOG_GROUPS.research }],
    },
    {
      name: 'synthesis',
      detail: objective.synthesisDetail,
      error: objective.synthesisError,
      logs: [{ label: 'synthesise', group: LOG_GROUPS.synthesise }],
    },
    {
      name: 'items',
      detail: objective.itemsDetail,
      error: objective.itemsError,
      logs: [
        { label: 'submit', group: LOG_GROUPS.itemsSubmit },
        { label: 'poll', group: LOG_GROUPS.itemsPoll },
        { label: 'retrieve', group: LOG_GROUPS.itemsRetrieve },
      ],
    },
  ];

  return (
    <dl className="facts">
      {passes.map((pass) => (
        <div key={pass.name}>
          <dt>{pass.name}</dt>
          <dd className={pass.error ? 'bad' : undefined}>
            {pass.error ? `failed: ${pass.error}` : (pass.detail ?? '—')}
            <span className="sub">
              {' '}
              logs (last 24h):{' '}
              {pass.logs.map((log, index) => (
                <span key={log.group}>
                  {index > 0 && ' · '}
                  <a
                    className="admin-link"
                    href={logsRecent(log.group)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {log.label}
                  </a>
                </span>
              ))}
            </span>
          </dd>
        </div>
      ))}
    </dl>
  );
}

export default function Objective() {
  const { objectiveId } = useParams<{ objectiveId: string }>();
  const [detail, setDetail] = useState<AdminObjectiveDetail>();
  const [error, setError] = useState<ApiError>();

  useEffect(() => {
    if (!objectiveId) return;
    let stale = false;
    setDetail(undefined);
    setError(undefined);
    void getObjective(objectiveId).then((result) => {
      if (stale) return;
      if (isError(result)) setError(result);
      else setDetail(result);
    });
    return () => {
      stale = true;
    };
  }, [objectiveId]);

  if (!objectiveId) return null;
  if (error) return <Failed error={error} what={`Objective ${objectiveId}`} />;
  if (!detail) return <Waiting what="objective" />;

  const objective = detail.objective;

  return (
    <>
      <nav className="crumbs">
        <Link className="admin-link" to="/admin">
          learners
        </Link>
        <span aria-hidden="true">/</span>
        {objective.userId ? (
          <Link className="admin-link" to={`/admin/learner/${objective.userId}`}>
            learner
          </Link>
        ) : (
          <span>learner</span>
        )}
        <span aria-hidden="true">/</span>
        <span>{localTitle(objective.title, objectiveId)}</span>
      </nav>

      <section className="admin-panel">
        <header className="admin-head">
          <h1>{localTitle(objective.title)}</h1>
          <p className="sub">
            <span className={`tag status-${objective.status}`}>{objective.status}</span>{' '}
            <Id value={objectiveId} /> ·{' '}
            {objective.activatedAt ? `started ${ago(objective.activatedAt)}` : 'never started'}
          </p>
        </header>

        {objective.description && <p className="quoted">{objective.description}</p>}

        <dl className="facts">
          <div>
            <dt>Locale</dt>
            <dd>{objective.locale ?? '—'}</dd>
          </div>
          <div>
            <dt>Created</dt>
            <dd>{objective.createdAt ? stamp(objective.createdAt) : '—'}</dd>
          </div>
          <div>
            <dt>curriculumVersion</dt>
            <dd>{objective.curriculumVersion ?? '—'}</dd>
          </div>
          <div>
            <dt>itemVersion</dt>
            <dd>{objective.itemVersion ?? '—'}</dd>
          </div>
        </dl>

        <PassState detail={detail} />

        <div className="admin-actions">
          <Link className="admin-button" to={`/admin/objective/${objectiveId}/items`}>
            Inspect items
          </Link>
        </div>
      </section>

      {/*
        The three re-runs, each with its price on the button and again in the
        confirmation. The prices come from `PASS_COSTS`, which cites docs/costs.md so
        the two get updated together — a console that hides cost spends the budget by
        accident, and research alone is a fifth of a month's infrastructure bill.
      */}
      <section className="admin-panel">
        <header className="admin-head">
          <h2>Re-run a pass</h2>
          <p className="sub">
            Each one spends model budget. Figures are measured, from docs/costs.md.
          </p>
        </header>
        <div className="admin-actions">
          {(['research', 'synthesis', 'items'] as const).map((pass) => (
            <ConfirmedAction
              key={pass}
              label={`Re-run ${pass}`}
              cost={PASS_COSTS[pass].dollars}
              consequence={`It ${PASS_COSTS[pass].what}.`}
              basis={PASS_COSTS[pass].basis}
              run={() => rerun(objectiveId, pass)}
            />
          ))}
        </div>
      </section>

      {/* `?? []` on all three: an API that omits a key it is documented to return
          must cost one empty panel, not the whole objective screen. */}
      <Tree nodes={detail.nodes ?? []} objectiveId={objectiveId} />
      <ResearchDocs research={detail.research ?? []} />
      <Manifests manifests={detail.manifests ?? []} />
    </>
  );
}
